/**
 * CountrySelectWrapper — countries-list + SelectWrapper 래퍼.
 * countries-list import는 이 파일에서만 허용.
 *
 * 기능:
 * - <select data-country-select> 에 나라 목록 자동 주입
 * - locale hidden input 자동 생성 (name + "_locale", ISO 2-letter code)
 * - SelectWrapper(SlimSelect)로 검색 가능한 드롭다운 UI 적용
 * - 국가 변경 시 locale hidden input 자동 업데이트
 */

import { countries } from "countries-list";
import { SelectWrapper } from "./SelectWrapper";

type CountryCode = keyof typeof countries;

export interface CountrySelectOptions {
    placeholder?: string;
    searchPlaceholder?: string;
    /** 목록 상단에 먼저 표시할 ISO 2-letter 코드 배열 */
    prioritized?: CountryCode[];
}

export class CountrySelectWrapper {
    private localeInput: HTMLInputElement | null = null;

    constructor(
        private readonly select: HTMLSelectElement,
        private readonly options: CountrySelectOptions = {}
    ) {}

    init(): this {
        this._populate();
        this._insertLocale();

        new SelectWrapper(this.select, {
            placeholder: this.options.placeholder,
            searchPlaceholder: this.options.searchPlaceholder ?? "국가 검색...",
        }).init();

        this.select.addEventListener("change", () => {
            if (this.localeInput) {
                this.localeInput.value = this.select.value;
            }
        });

        return this;
    }

    private _populate(): void {
        const codes = Object.keys(countries) as CountryCode[];
        const prioritized = this.options.prioritized ?? [];
        const rest = codes
            .filter((c) => !prioritized.includes(c))
            .sort((a, b) => countries[a].name.localeCompare(countries[b].name));

        const currentVal = this.select.value;

        [...prioritized, ...rest].forEach((iso2) => {
            const opt = document.createElement("option");
            opt.value = iso2;
            opt.textContent = countries[iso2].name;
            if (iso2 === currentVal) opt.selected = true;
            this.select.appendChild(opt);
        });
    }

    private _insertLocale(): void {
        const hidden = document.createElement("input");
        hidden.type = "hidden";
        hidden.name = (this.select.name || "") + "_locale";
        hidden.value = this.select.value ?? "";
        this.select.insertAdjacentElement("afterend", hidden);
        this.localeInput = hidden;
    }
}

export function initCountrySelects(root: Element | Document = document): void {
    root
        .querySelectorAll<HTMLSelectElement>("[data-country-select]:not([data-country-inited])")
        .forEach((select) => {
            select.dataset.countryInited = "1";
            new CountrySelectWrapper(select).init();
        });
}
